import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { PricingChange } from './pricingRecap'

interface Props {
  isOpen: boolean
  customerName: string
  changes: PricingChange[]
  isSaving: boolean
  onConfirm(): void
  onClose(): void
}

const ConfirmPricingModalComponent = ({
  isOpen,
  customerName,
  changes,
  isSaving,
  onConfirm,
  onClose,
}: Props) => {
  /**
   * Both close paths are refused while the request is in flight. The PUT cannot actually be
   * recalled, so a live "Cancel" would dismiss the dialog while the pricing change still lands
   * — and because the caller clears its pending values on close, the failure path would then
   * have no dialog left to reopen. Refusing to close is honest: the only states are "not saved
   * yet" and "saved".
   */
  const handleClose = useCallback(() => {
    if (isSaving) {
      return
    }

    onClose()
  }, [isSaving, onClose])

  return (
    <CommonModal
      isOpen={isOpen}
      title="Change live pricing?"
      onClose={handleClose}
      buttons={
        <Fragment>
          <Button size="small" color="grey" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            size="small"
            color="dark"
            onClick={onConfirm}
            loading={isSaving}
            data-testid="confirm-pricing"
          >
            Save changes
          </Button>
        </Fragment>
      }
    >
      <Typography variant="body2" css={tw`mb-4 text-shades-500`}>
        This changes live payout pricing for {customerName} and affects real contractor payouts.
      </Typography>
      <div css={tw`flex flex-col gap-2`}>
        {changes.map((change, index) => {
          return (
            <div key={index} css={tw`flex items-baseline justify-between gap-4`}>
              <Typography variant="body2" css={tw`text-shades-500`}>
                {change.label}
              </Typography>
              <Typography variant="body2" css={tw`text-shades-900`}>
                {change.from} → {change.to}
              </Typography>
            </div>
          )
        })}
      </div>
    </CommonModal>
  )
}

export const ConfirmPricingModal = memo(ConfirmPricingModalComponent)
